import React from "react";
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  useTheme,
} from "@mui/material";
import CustomTypography from "../common/form/CustomTypography";
import brandingIcon from "../../../assets/swagstore/pallette-icon.png";
import bannerIcon from "../../../assets/swagstore/image-icon.png";
import loginIcon from "../../../assets/swagstore/exit-icon.png";
import mappingIcon from "../../../assets/swagstore/connect-wire.png";
import PhotoCameraOutlinedIcon from "@mui/icons-material/PhotoCameraOutlined";
import { SWAGSTORE_STEPS } from "../../../constants/SwagstoreConstants";

interface MenuItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  description: string;
}

interface LeftSidebarProps {
  selectedItem: string;
  onItemSelect: (itemId: string) => void;
  menuItems?: MenuItem[];
  storeId?: string | null;
}

const defaultMenuItems: MenuItem[] = [
  {
    id: SWAGSTORE_STEPS.STORE_DETAILS,
    label: "Store Details",
    icon: <img src={brandingIcon} alt="Branding" />,
    description: "Basic information",
  },
  {
    id: SWAGSTORE_STEPS.BRANDING,
    label: "Branding & Banner",
    icon: <img src={bannerIcon} alt="Banner & Hero" />,
    description: "Branding &Banner",
  },
  {
    id: SWAGSTORE_STEPS.LOGIN,
    label: "Login page",
    icon: <img src={loginIcon} alt="Login page" />,
    description: "Image, Layout",
  },
  {
    id: SWAGSTORE_STEPS.PRODUCT_MAPPING,
    label: "Products Mapping",
    icon: <img src={mappingIcon} alt="Products Mapping" />,
    description: "Select Products, set price",
  },
  {
    id: SWAGSTORE_STEPS.MOCKUPS,
    label: "Mockups",
    icon: (
      <PhotoCameraOutlinedIcon
        sx={{ fontSize: 28, color: "text.secondary" }}
        aria-hidden
      />
    ),
    description: "Logo & PSD mockups",
  },
];

const LeftSidebar: React.FC<LeftSidebarProps> = ({
  selectedItem,
  onItemSelect,
  menuItems = defaultMenuItems,
  storeId,
}) => {
  const theme = useTheme();

  const isItemDisabled = (itemId: string): boolean => {
    // STORE_DETAILS is always enabled
    if (itemId === SWAGSTORE_STEPS.STORE_DETAILS) {
      return false;
    }
    // All other tabs are disabled if storeId is not present
    return !storeId;
  };

  return (
    <Box
      sx={{
        width: "300px",
        height: "100vh",
        backgroundColor: theme.palette.background.paper,
        borderRight: `1px solid ${theme.palette.divider}`,
        overflowY: "auto",
      }}
    >
      <Box sx={{ p: 3 }}>
        <CustomTypography
          variant="heading6"
          fontWeight={600}
          style={{ marginBottom: 8 }}
        >
          CUSTOMIZATION
        </CustomTypography>
        <CustomTypography
          variant="heading9"
          color={theme.palette.grey[600]}
          style={{ marginBottom: 20 }}
        >
          Configure every aspect of store
        </CustomTypography>

        <List sx={{ p: 0 }}>
          {menuItems.map((item) => {
            const disabled = isItemDisabled(item.id);
            return (
              <ListItem key={item.id} disablePadding sx={{ mb: 1 }}>
                <ListItemButton
                  selected={selectedItem === item.id}
                  disabled={disabled}
                  onClick={() => {
                    if (!disabled) {
                      onItemSelect(item.id);
                    }
                  }}
                  sx={{
                    borderRadius: "8px",
                    position: "relative",
                    opacity: disabled ? 0.5 : 1,
                    cursor: disabled ? "not-allowed" : "pointer",
                    "&.Mui-selected": {
                      backgroundColor: theme.palette.customColors.white,
                      boxShadow: `${theme.palette.customColors.shadowLight} 0px 1px 3px, ${theme.palette.customColors.shadowMedium} 0px 1px 2px`,
                      borderTopRightRadius: "15px",
                      borderBottomRightRadius: "15px",
                      "&::before": {
                        content: '""',
                        position: "absolute",
                        left: 0,
                        top: 0,
                        bottom: 0,
                        width: "4px",
                        backgroundColor: theme.palette.customColors.purple,
                      },
                      "&:hover": {
                        backgroundColor: "rgba(255, 255, 255, 1)" + "30",
                      },
                    },
                    "&:hover": {
                      backgroundColor: disabled
                        ? "transparent"
                        : theme.palette.action.hover,
                      borderTopRightRadius: "15px",
                      borderBottomRightRadius: "15px",
                      "&::before": {
                        content: disabled ? "none" : '""',
                        position: "absolute",
                        left: 0,
                        top: 0,
                        bottom: 0,
                        width: "4px",
                        backgroundColor: theme.palette.customColors.purple,
                      },
                    },
                    "&.Mui-disabled": {
                      opacity: 0.5,
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      color: disabled
                        ? theme.palette.action.disabled
                        : selectedItem === item.id
                        ? theme.palette.primary.main
                        : theme.palette.text.secondary,
                      width: 36,
                      height: 36,
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <CustomTypography
                        variant="body2"
                        fontWeight={selectedItem === item.id ? 600 : 400}
                        color={
                          disabled
                            ? theme.palette.action.disabled
                            : selectedItem === item.id
                            ? theme.palette.customColors.blackBorder
                            : theme.palette.text.primary
                        }
                      >
                        {item.label}
                      </CustomTypography>
                    }
                    secondary={
                      <CustomTypography
                        variant="body4"
                        color={disabled ? "action.disabled" : "text.secondary"}
                        fontWeight={600}
                        style={{ marginTop: 7 }}
                      >
                        {item.description}
                      </CustomTypography>
                    }
                  />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      </Box>
    </Box>
  );
};

export default LeftSidebar;
